import { supabase } from "@/infrastructure/supabase/client";
import type { CandidateRow } from "@/infrastructure/supabase/database.types";

export interface UpsertCandidateInput {
  id?: string;
  slug: string;
  name: string;
  narrative_md: string | null;
  commitments_md: string | null;
  pros_md: string | null;
  cons_md: string | null;
  time_to_mvp_weeks: number | null;
  tco_low_usd: number | null;
  tco_high_usd: number | null;
}

export const candidatesRepository = {
  async list(): Promise<CandidateRow[]> {
    const { data, error } = await supabase
      .from("candidates")
      .select("*")
      .order("name", { ascending: true });
    if (error) throw error;
    return data ?? [];
  },

  async getBySlug(slug: string): Promise<CandidateRow | null> {
    const { data, error } = await supabase
      .from("candidates")
      .select("*")
      .eq("slug", slug)
      .maybeSingle();
    if (error) throw error;
    return data;
  },

  async upsert(input: UpsertCandidateInput): Promise<CandidateRow> {
    const { data, error } = await supabase
      .from("candidates")
      .upsert(input, { onConflict: "slug" })
      .select("*")
      .single();
    if (error) throw error;
    return data;
  },

  async remove(id: string): Promise<void> {
    const { error } = await supabase.from("candidates").delete().eq("id", id);
    if (error) throw error;
  },

  async duplicate(sourceId: string, newSlug: string, newName: string): Promise<CandidateRow> {
    const { data: source, error: srcErr } = await supabase
      .from("candidates")
      .select("*")
      .eq("id", sourceId)
      .single();
    if (srcErr) throw srcErr;

    const { data: created, error: insErr } = await supabase
      .from("candidates")
      .insert({
        slug: newSlug,
        name: newName,
        narrative_md: source.narrative_md,
        commitments_md: source.commitments_md,
        pros_md: source.pros_md,
        cons_md: source.cons_md,
        time_to_mvp_weeks: source.time_to_mvp_weeks,
        tco_low_usd: source.tco_low_usd,
        tco_high_usd: source.tco_high_usd,
      })
      .select("*")
      .single();
    if (insErr) throw insErr;

    const { data: choices, error: choicesErr } = await supabase
      .from("candidate_choices")
      .select("component_id, product_id, override_notes_md")
      .eq("candidate_id", sourceId);
    if (choicesErr) throw choicesErr;

    if (choices && choices.length > 0) {
      const { error: copyErr } = await supabase.from("candidate_choices").insert(
        choices.map((c) => ({
          candidate_id: created.id,
          component_id: c.component_id,
          product_id: c.product_id,
          override_notes_md: c.override_notes_md,
        }))
      );
      if (copyErr) throw copyErr;
    }

    return created;
  },
};
